import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { getBaseUrl } from "@/lib/trpc";
import type { ChatSSEEvent } from "@/server/chat/types";

export interface ChatMessageUI {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface ToolCallState {
  name: string;
  id: string;
}

interface UseChatStreamOptions {
  collectionId: string;
}

export function useChatStream({ collectionId }: UseChatStreamOptions) {
  const [messages, setMessages] = useState<ChatMessageUI[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentToolCall, setCurrentToolCall] = useState<ToolCallState | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const prevCollectionIdRef = useRef(collectionId);

  // Reset on collection change
  useEffect(() => {
    if (prevCollectionIdRef.current !== collectionId) {
      prevCollectionIdRef.current = collectionId;
      setMessages([]);
      setIsStreaming(false);
      setCurrentToolCall(null);
      setError(null);
      abortRef.current?.abort();
      abortRef.current = null;
    }
  }, [collectionId]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming || !content.trim()) return;

      setError(null);

      const userMessage: ChatMessageUI = {
        id: crypto.randomUUID(),
        role: "user",
        content: content.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);

      const assistantId = crypto.randomUUID();
      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", content: "" },
      ]);

      const abortController = new AbortController();
      abortRef.current = abortController;

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;

        if (!token) {
          throw new Error("Not authenticated");
        }

        // Build message history (exclude the empty assistant message we just added)
        const history = [...messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const response = await fetch(`${getBaseUrl()}/api/chat-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            collectionId,
            messages: history,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            (err as any).error || `HTTP ${response.status}`,
          );
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonStr = line.slice(6);
              try {
                const event: ChatSSEEvent = JSON.parse(jsonStr);

                switch (event.type) {
                  case "delta":
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === assistantId
                          ? { ...m, content: m.content + event.content }
                          : m,
                      ),
                    );
                    break;
                  case "tool_call":
                    setCurrentToolCall({
                      name: event.name,
                      id: event.id,
                    });
                    break;
                  case "tool_result":
                    setCurrentToolCall(null);
                    break;
                  case "done":
                    // Final content already accumulated via deltas
                    break;
                  case "error":
                    setError(event.message);
                    break;
                }
              } catch {
                // Skip malformed JSON lines
              }
            }
          }
        }
      } catch (e: any) {
        if (e.name !== "AbortError") {
          setError(e.message || "Connection failed");
        }
      } finally {
        setIsStreaming(false);
        setCurrentToolCall(null);
        abortRef.current = null;
      }
    },
    [collectionId, isStreaming, messages],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setIsStreaming(false);
    setCurrentToolCall(null);
    setError(null);
  }, []);

  return {
    messages,
    isStreaming,
    currentToolCall,
    error,
    sendMessage,
    stop,
    reset,
  };
}
